import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, getErrorMessage } from '../lib/api';
import { toast } from '../utils/toast';
import { Breadcrumbs, BreadcrumbItem } from '../components/ui/Breadcrumbs';
import { LoadingState, EmptyState, Alert, Pagination, ExportButton } from '../components/ui';
import type { SupportTicket } from '../types';
import { X, LifeBuoy, Plus, MessageSquare, Send, Clock, AlertCircle, ArrowRight } from 'lucide-react';

export const Support: React.FC = () => {
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [replyMessage, setReplyMessage] = useState('');

  // Filters state
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    category: '',
    search: '',
  });
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(15);

  // Close modals on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowCreateModal(false);
        setSelectedTicket(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search);
      setOffset(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [filters.search]);

  // Reset offset when filters change
  useEffect(() => {
    setOffset(0);
  }, [filters.status, filters.priority, filters.category]);

  const [formData, setFormData] = useState({
    subject: '',
    category: 'technical' as 'technical' | 'billing' | 'general',
    priority: 'medium' as 'low' | 'medium' | 'high' | 'critical',
    description: '',
  });

  const { data: ticketsData, isLoading } = useQuery({
    queryKey: [
      'tickets',
      filters.status,
      filters.priority,
      filters.category,
      debouncedSearch,
      offset,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.priority) params.append('priority', filters.priority);
      if (filters.category) params.append('category', filters.category);
      if (debouncedSearch) params.append('search', debouncedSearch);
      params.append('limit', limit.toString());
      params.append('offset', offset.toString());

      const response = await api.get<{
        data: SupportTicket[];
        meta: { total: number; limit: number; offset: number };
      }>(`/tickets?${params.toString()}`);
      return response.data;
    },
  });

  const tickets = ticketsData?.data;
  const total = ticketsData?.meta?.total || 0;

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      priority: '',
      category: '',
      search: '',
    });
    setOffset(0);
  };

  const hasActiveFilters = Object.values(filters).some((v) => v !== '');

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const response = await api.post('/tickets', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      setShowCreateModal(false);
      setFormData({
        subject: '',
        category: 'technical',
        priority: 'medium',
        description: '',
      });
      toast.success('Ticket de suporte criado com sucesso!');
    },
  });

  const replyMutation = useMutation({
    mutationFn: async (data: { ticket_id: string; message: string }) => {
      const response = await api.post(`/tickets/${data.ticket_id}/responses`, {
        message: data.message,
      });
      return response.data;
    },
    onSuccess: (resData, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      setReplyMessage('');
      toast.success('Resposta enviada com sucesso!');
      const newResponse = resData?.data || {
        id: `temp-${Date.now()}`,
        ticket_id: variables.ticket_id,
        message: variables.message,
        is_staff: false,
        created_at: new Date().toISOString(),
      };
      setSelectedTicket((prev) =>
        prev && prev.id === variables.ticket_id
          ? {
              ...prev,
              responses: [...(prev.responses || []), newResponse],
            }
          : prev
      );
    },
  });

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate(formData);
  };

  const handleReply = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedTicket && replyMessage.trim()) {
      replyMutation.mutate({
        ticket_id: selectedTicket.id,
        message: replyMessage,
      });
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical':
        return 'badge-error';
      case 'high':
        return 'badge-warning';
      case 'medium':
        return 'badge-primary';
      default:
        return 'badge-success';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'badge-primary';
      case 'in_progress':
        return 'badge-warning';
      case 'waiting_for_customer':
        return 'badge-warning';
      case 'resolved':
        return 'badge-success';
      case 'closed':
        return 'badge-gray';
      default:
        return 'badge-primary';
    }
  };

  return (
    <div className="container-custom py-8">
      {/* Breadcrumbs */}
      <Breadcrumbs className="mb-6">
        <BreadcrumbItem href="/">Início</BreadcrumbItem>
        <BreadcrumbItem>Suporte</BreadcrumbItem>
      </Breadcrumbs>

      {/* Header */}
      <div className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2 flex items-center gap-2.5">
            <LifeBuoy className="w-8 h-8 text-primary-500" />
            <span>Suporte</span>
          </h1>
          <p className="text-slate-600 dark:text-slate-400 text-base">
            Obtenha ajuda com questões técnicas ou de faturação
          </p>
        </div>
        <div className="flex gap-2">
          <ExportButton
            data={tickets || []}
            filename={`support_tickets_${Date.now()}.csv`}
            headers={[
              { key: 'ticket_number', label: 'Ticket #' },
              { key: 'subject', label: 'Assunto' },
              { key: 'category', label: 'Categoria' },
              { key: 'priority', label: 'Prioridade' },
              { key: 'status', label: 'Estado' },
              { key: 'created_at', label: 'Criado em' },
            ]}
          />
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-base btn-md btn-primary flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            <span>Criar Ticket</span>
          </button>
        </div>
      </div>

      {/* Create Ticket Modal */}
      {showCreateModal && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs z-50 p-4 animate-fade-in"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowCreateModal(false);
            }
          }}
        >
          <div className="bg-white dark:bg-slate-900 max-w-2xl w-full rounded-2xl p-6 sm:p-8 shadow-2xl border border-slate-200 dark:border-slate-800 max-h-[90vh] overflow-y-auto animate-scale-in">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2.5">
                <LifeBuoy className="w-6 h-6 text-primary-500" />
                <span>Criar Ticket de Suporte</span>
              </h2>
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                title="Fechar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreate}>
              <div className="space-y-4">
                <div>
                  <label htmlFor="subject" className="label-modern">
                    Assunto *
                  </label>
                  <input
                    id="subject"
                    type="text"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    className="input-modern"
                    placeholder="Descreva o problema brevemente"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="category" className="label-modern">
                      Categoria *
                    </label>
                    <select
                      id="category"
                      value={formData.category}
                      onChange={(e) =>
                        setFormData({ ...formData, category: e.target.value as any })
                      }
                      className="input-modern"
                    >
                      <option value="technical">Técnico</option>
                      <option value="billing">Faturação</option>
                      <option value="general">Geral</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="priority" className="label-modern">
                      Prioridade *
                    </label>
                    <select
                      id="priority"
                      value={formData.priority}
                      onChange={(e) =>
                        setFormData({ ...formData, priority: e.target.value as any })
                      }
                      className="input-modern"
                    >
                      <option value="low">Baixa</option>
                      <option value="medium">Média</option>
                      <option value="high">Alta</option>
                      <option value="critical">Crítica</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="description" className="label-modern">
                    Descrição *
                  </label>
                  <textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="input-modern"
                    rows={6}
                    placeholder="Descreva o problema em detalhe..."
                    required
                    style={{ resize: 'vertical' }}
                  />
                </div>
              </div>

              {createMutation.isError && (
                <Alert variant="error" className="mt-4">
                  {getErrorMessage(createMutation.error)}
                </Alert>
              )}

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="btn-base btn-md btn-secondary flex-1"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="btn-base btn-md btn-primary flex-1"
                >
                  {createMutation.isPending ? (
                    <span className="flex items-center gap-2">
                      <span className="spinner"></span>
                      Criando...
                    </span>
                  ) : (
                    'Criar Ticket'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Ticket Detail Modal */}
      {selectedTicket && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs z-50 p-4 animate-fade-in"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setSelectedTicket(null);
            }
          }}
        >
          <div className="bg-white dark:bg-slate-900 max-w-3xl w-full rounded-2xl p-6 sm:p-8 shadow-2xl border border-slate-200 dark:border-slate-800 max-h-[90vh] overflow-y-auto animate-scale-in">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-1">
                  {selectedTicket.subject}
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Ticket #{selectedTicket.ticket_number}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedTicket(null)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                title="Fechar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex flex-wrap gap-2 mb-6">
              <span className={`badge-modern ${getStatusColor(selectedTicket.status)}`}>
                {selectedTicket.status === 'open'
                  ? 'Aberto'
                  : selectedTicket.status === 'in_progress'
                    ? 'Em Progresso'
                    : selectedTicket.status === 'waiting_for_customer'
                      ? 'Aguardando Cliente'
                      : selectedTicket.status === 'resolved'
                        ? 'Resolvido'
                        : 'Fechado'}
              </span>
              <span className={`badge-modern ${getPriorityColor(selectedTicket.priority)}`}>
                {selectedTicket.priority === 'low'
                  ? 'Baixa'
                  : selectedTicket.priority === 'medium'
                    ? 'Média'
                    : selectedTicket.priority === 'high'
                      ? 'Alta'
                      : 'Crítica'}
              </span>
              <span className="badge-modern badge-gray">
                {selectedTicket.category === 'technical'
                  ? 'Técnico'
                  : selectedTicket.category === 'billing'
                    ? 'Faturação'
                    : 'Geral'}
              </span>
            </div>

            {/* Conversation */}
            <div className="mb-6 p-4 max-h-96 overflow-y-auto rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/40">
              {selectedTicket.responses && selectedTicket.responses.length > 0 ? (
                <div className="space-y-4">
                  {selectedTicket.responses.map((response) => (
                    <div
                      key={response.id}
                      className={`p-4 rounded-xl border transition-all ${
                        response.is_staff
                          ? 'ml-6 bg-primary-50/70 dark:bg-primary-950/30 border-primary-200/60 dark:border-primary-800/40'
                          : 'mr-6 bg-white dark:bg-slate-800/80 border-slate-200 dark:border-slate-700/60'
                      }`}
                    >
                      <div className="flex justify-between items-center mb-2">
                        <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-800 dark:text-slate-200">
                          {response.is_staff ? (
                            <>
                              <LifeBuoy className="w-3.5 h-3.5 text-primary-600 dark:text-primary-400" />
                              <span className="text-primary-700 dark:text-primary-300">Equipa de Suporte</span>
                            </>
                          ) : (
                            <>
                              <MessageSquare className="w-3.5 h-3.5 text-slate-500" />
                              <span>Você</span>
                            </>
                          )}
                        </span>
                        <span className="text-xs text-slate-400 dark:text-slate-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(response.created_at).toLocaleString('pt-BR')}
                        </span>
                      </div>
                      <p className="text-slate-700 dark:text-slate-300 text-sm whitespace-pre-wrap leading-relaxed">
                        {response.message}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                  <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">Nenhuma resposta ainda.</p>
                </div>
              )}
            </div>

            {/* Reply Form */}
            {selectedTicket.status !== 'closed' && (
              <form onSubmit={handleReply}>
                <div className="mb-4">
                  <label htmlFor="reply" className="label-modern">
                    Sua Resposta
                  </label>
                  <textarea
                    id="reply"
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    className="input-modern"
                    rows={4}
                    placeholder="Digite sua mensagem..."
                    required
                    style={{ resize: 'vertical' }}
                  />
                </div>

                {replyMutation.isError && (
                  <Alert variant="error" className="mb-4">
                    {getErrorMessage(replyMutation.error)}
                  </Alert>
                )}

                <button
                  type="submit"
                  disabled={replyMutation.isPending}
                  className="btn-base btn-md btn-primary w-full flex items-center justify-center gap-2"
                >
                  {replyMutation.isPending ? (
                    <>
                      <span className="spinner"></span>
                      <span>Enviando...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      <span>Enviar Resposta</span>
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Tickets List */}
      {isLoading ? (
        <LoadingState type="card" count={3} />
      ) : tickets && tickets.length > 0 ? (
        <>
          {/* Filters */}
          <div className="card-modern p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div>
                <input
                  type="text"
                  placeholder="Pesquisar por assunto ou #..."
                  className="input-modern"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                />
              </div>
              <div>
                <select
                  className="input-modern"
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <option value="">Todos os Status</option>
                  <option value="open">Aberto</option>
                  <option value="in_progress">Em Progresso</option>
                  <option value="waiting_for_customer">Aguardando</option>
                  <option value="resolved">Resolvido</option>
                  <option value="closed">Fechado</option>
                </select>
              </div>
              <div>
                <select
                  className="input-modern"
                  value={filters.priority}
                  onChange={(e) => handleFilterChange('priority', e.target.value)}
                >
                  <option value="">Todas as Prioridades</option>
                  <option value="low">Baixa</option>
                  <option value="medium">Média</option>
                  <option value="high">Alta</option>
                  <option value="critical">Crítica</option>
                </select>
              </div>
              <div>
                <select
                  className="input-modern"
                  value={filters.category}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                >
                  <option value="">Todas as Categorias</option>
                  <option value="technical">Técnico</option>
                  <option value="billing">Faturação</option>
                  <option value="general">Geral</option>
                </select>
              </div>
            </div>
            {hasActiveFilters && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600 dark:text-slate-400">
                  {total} {total === 1 ? 'ticket encontrado' : 'tickets encontrados'}
                </span>
                <button
                  onClick={clearFilters}
                  className="btn-base btn-sm text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/30 border border-rose-300 dark:border-rose-800/60"
                >
                  Limpar Filtros
                </button>
              </div>
            )}
          </div>

          <div className="space-y-4">
            {tickets.map((ticket) => (
              <div
                key={ticket.id}
                onClick={() => setSelectedTicket(ticket)}
                className="card-modern p-6 cursor-pointer group hover:border-primary-500/50 hover:shadow-md transition-all"
              >
                <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                      {ticket.subject}
                    </h3>
                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className={`badge-modern ${getStatusColor(ticket.status)}`}>
                        {ticket.status === 'open'
                          ? 'Aberto'
                          : ticket.status === 'in_progress'
                            ? 'Em Progresso'
                            : ticket.status === 'waiting_for_customer'
                              ? 'Aguardando'
                              : ticket.status === 'resolved'
                                ? 'Resolvido'
                                : 'Fechado'}
                      </span>
                      <span className={`badge-modern ${getPriorityColor(ticket.priority)}`}>
                        {ticket.priority === 'low'
                          ? 'Baixa'
                          : ticket.priority === 'medium'
                            ? 'Média'
                            : ticket.priority === 'high'
                              ? 'Alta'
                              : 'Crítica'}
                      </span>
                      <span className="badge-modern badge-gray">
                        {ticket.category === 'technical'
                          ? 'Técnico'
                          : ticket.category === 'billing'
                            ? 'Faturação'
                            : 'Geral'}
                      </span>
                    </div>
                    <p className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500" />
                      <span>
                        Ticket #{ticket.ticket_number} • Criado em{' '}
                        {new Date(ticket.created_at).toLocaleDateString('pt-BR')}
                      </span>
                    </p>
                  </div>
                  <span className="text-sm font-semibold text-primary-600 dark:text-primary-400 group-hover:translate-x-0.5 transition-transform inline-flex items-center gap-1">
                    <span>Ver</span>
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6">
            <Pagination
              total={total}
              limit={limit}
              offset={offset}
              onPageChange={setOffset}
              onLimitChange={setLimit}
            />
          </div>
        </>
      ) : (
        <EmptyState
          icon={<MessageSquare className="w-16 h-16" />}
          title={hasActiveFilters ? 'Nenhum ticket encontrado' : 'Nenhum ticket de suporte'}
          description={
            hasActiveFilters
              ? 'Tente ajustar os filtros para ver mais resultados.'
              : 'Você ainda não criou nenhum ticket. Se precisar de ajuda, crie um ticket e nossa equipa responderá em breve.'
          }
          action={
            hasActiveFilters ? (
              <button onClick={clearFilters} className="btn-base btn-md btn-secondary">
                Limpar Filtros
              </button>
            ) : (
              <button
                onClick={() => setShowCreateModal(true)}
                className="btn-base btn-md btn-primary"
              >
                Criar Primeiro Ticket
              </button>
            )
          }
        />
      )}
    </div>
  );
};
